import { TextField } from "@material-ui/core";
import { useInfiniteGetOfferLogs } from "../../../services";
import { useLogsTableColumns } from "../../hooks";
import { useTranslation } from "react-i18next";
import {
  CarrierSelect,
  LocationMarketsSelect,
  LogStatusSelect,
  OrderSelect,
} from "../search-field-selects";
import {
  CarrierT,
  LocationT,
  LogStatusT,
  OrderT,
  Zip3MarketsT,
} from "../../../../domain";
import {
  CloseIcon,
  Table,
  Tabs,
} from "../../../../../../shared/infrastructure";
import React, { useCallback, useMemo, useState } from "react";

type LogFiltersT = Partial<{
  carrier: CarrierT;
  origin: LocationT;
  destination: LocationT;
  ship_date: string;
  delivery_date: string;
  order: OrderT;
  status: LogStatusT;
  origin_market: Zip3MarketsT;
  destination_market: Zip3MarketsT;
}>;

type LogFiltersProps = {
  filters: LogFiltersT;
  setFilters: (filters: LogFiltersT) => void;
};

const LogFilters = ({
  filters,
  setFilters,
}: LogFiltersProps): React.ReactElement => {
  const setFilter = useCallback<
    <N extends keyof LogFiltersT, V extends LogFiltersT[N]>(
      name: N,
      value: V
    ) => void
  >(
    (name, value) => {
      setFilters({
        ...filters,
        [name]: value,
      });
    },
    [filters, setFilters]
  );

  return (
    <section className="grid auto-cols-fr grid-flow-col gap-[20px]">
      <div>
        <CarrierSelect
          name="carrier"
          keyProp="carrier_id"
          labelProp="carrier_id"
          selectedValue={filters.carrier}
          onChangeValue={(value) => {
            if (!Array.isArray(value)) {
              setFilter("carrier", value ? value : undefined);
            }
          }}
          placeholder="Carrier"
          noError
        />
      </div>
      <div>
        <OrderSelect
          name="order"
          keyProp="orderNumber"
          placeholder="Order Number"
          selectedValue={filters.order}
          onChangeValue={(value) => {
            if (!Array.isArray(value)) {
              setFilter("order", value ? value : undefined);
            }
          }}
          noError
        />
      </div>
      {/**
      <div>
        <LocationSelect
          name="origin"
          keyProp="city"
          customKeyProp={(option) =>
            `${option.city}, ${option.state_id ?? option.state_code}`
          }
          selectedValue={filters.origin}
          onChangeValue={(value) => {
            if (!Array.isArray(value)) {
              setFilter("origin", value ? value : undefined);
            }
          }}
          placeholder="Origin"
          noError
        />
      </div>
      <div>
        <LocationSelect
          name="destination"
          keyProp="city"
          customKeyProp={(option) =>
            `${option.city}, ${option.state_id ?? option.state_code}`
          }
          selectedValue={filters.destination}
          onChangeValue={(value) => {
            if (!Array.isArray(value)) {
              setFilter("destination", value ? value : undefined);
            }
          }}
          placeholder="Destination"
          noError
        />
      </div>

      **/}

      <div>
        <LocationMarketsSelect
          name="origin"
          keyProp="market"
          labelProp="market"
          selectedValue={filters.origin_market}
          onChangeValue={(value) => {
            if (!Array.isArray(value)) {
              setFilter("origin_market", value ? value : undefined);
            }
          }}
          placeholder="Origin Market"
          noError
        />
      </div>

      <div>
        <LocationMarketsSelect
          name="destination"
          keyProp="market"
          customKeyProp={(option) => `${option.zip3}, ${option.market}`}
          selectedValue={filters.destination_market}
          onChangeValue={(value) => {
            if (!Array.isArray(value)) {
              setFilter("destination_market", value ? value : undefined);
            }
          }}
          placeholder="Destination Market"
          noError
        />
      </div>

      <div>
        <TextField
          id="ship_date"
          type="date"
          className="mb-[16px] text-[14px] outline-0"
          value={filters.ship_date}
          placeholder="Ship Date"
          inputProps={{
            className: "px-[16px] py-[5px] placeholder:text-[#CFCFCF]",
            placeholder: "Ship Date",
          }}
          InputProps={{
            className: "h-[36px]",
            endAdornment: (
              <section className="grid grid-flow-col items-center gap-[5px]">
                {!!filters.ship_date && (
                  <button onClick={() => setFilter("ship_date", "")}>
                    <CloseIcon
                      width={16}
                      height={16}
                      className="min-w-[1rem] text-base text-gray-800"
                    />
                  </button>
                )}
              </section>
            ),
          }}
          onChange={(e) => setFilter("ship_date", e.target.value)}
        />
      </div>
      <div>
        <TextField
          id="delivery_date"
          type="date"
          className="mb-[16px] text-[14px] outline-0"
          value={filters.delivery_date}
          placeholder="Delivery Date"
          inputProps={{
            className: "px-[16px] py-[5px] placeholder:text-[#CFCFCF]",
            placeholder: "Delivery Date",
          }}
          InputProps={{
            className: "h-[36px]",
            endAdornment: (
              <section className="grid grid-flow-col items-center gap-[5px]">
                {!!filters.delivery_date && (
                  <button onClick={() => setFilter("delivery_date", "")}>
                    <CloseIcon
                      width={16}
                      height={16}
                      className="min-w-[1rem] text-base text-gray-800"
                    />
                  </button>
                )}
              </section>
            ),
          }}
          onChange={(e) => setFilter("delivery_date", e.target.value)}
        />
      </div>
      <div>
        <LogStatusSelect
          name="status"
          value={filters.status}
          onChange={(value) => {
            if (!Array.isArray(value)) {
              setFilter("status", value ? value : undefined);
            }
          }}
          placeholder="Status"
          noError
        />
      </div>
    </section>
  );
};

type LogsTableProps = {
  carrier: CarrierT;
  origin?: LocationT;
  destination?: LocationT;
  showStatus: boolean;
  section: "history" | "orders";
};

const LogsTable = ({
  carrier,
  origin,
  destination,
  section,
  showStatus,
}: LogsTableProps): React.ReactElement => {
  const { logsTableColumns, sortByValues } = useLogsTableColumns(showStatus);
  const [filters, setFilters] = useState<LogFiltersT>({
    carrier,
    /*
     * origin,
     * destination,
     */
    ...(origin?.market
      ? {
          origin_market: {
            market: origin?.market,
            zip3: origin?.zipCode || origin.zip_code,
          },
        }
      : {}),
    ...(destination?.market
      ? {
          destination_market: {
            market: destination?.market,
            zip3: destination?.zipCode || destination.zip_code,
          },
        }
      : {}),
  });

  const {
    allPages,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteGetOfferLogs(section, {
    ...sortByValues,
    ...(filters.carrier ? { carrier: filters.carrier.carrier_id } : {}),
    ...(filters.order?.orderNumber
      ? { order_number: filters.order?.orderNumber }
      : {}),
    ...(filters.ship_date ? { pick_up_date: filters.ship_date } : {}),
    ...(filters.delivery_date ? { drop_off_date: filters.delivery_date } : {}),
    /*
     * ...(filters.origin
     *   ? {
     *       origin: `${filters.origin.city},${
     *         filters.origin.state_code ?? filters.origin.state_id
     *       }`,
     *     }
     *   : {}),
     * ...(filters.destination
     *   ? {
     *       destination: `${filters.destination.city},${
     *         filters.destination.state_code ?? filters.destination.state_id
     *       }`,
     *     }
     *   : {}),
     */
    ...(filters.status?.id ? { status: filters.status.id } : {}),
    ...(filters.origin_market
      ? { origin_market: filters.origin_market?.market }
      : {}),
    ...(filters.destination_market
      ? { destination_market: filters.destination_market?.market }
      : {}),
  });

  return (
    <section className="grid gap-[15px]">
      <LogFilters filters={filters} setFilters={setFilters} />
      <Table
        columns={logsTableColumns}
        message="No history logs available"
        data={allPages ?? []}
        isLoading={isLoading || isFetchingNextPage}
        getNextPage={fetchNextPage}
        hasMorePages={!!hasNextPage}
        maxHeight={230}
        rowStyles={{
          borderBottom: 1,
          borderBottomStyle: "solid",
          borderBottomColor: "#F0F0F0",
        }}
      />
    </section>
  );
};

type OfferLogHistoryProps = {
  carrier: CarrierT;
  origin?: LocationT;
  destination?: LocationT;
};

export const OfferLogHistory = ({
  carrier,
  origin,
  destination,
}: OfferLogHistoryProps): React.ReactElement => {
  const [t] = useTranslation("global");
  const [value, setValue] = React.useState("approvals");

  const tabs = useMemo(
    () => [
      {
        label: t<string>(
          "context.private.components.offer-log-history.tabs.approvals"
        ),
        value: "approvals",
      },
      {
        label: t<string>(
          "context.private.components.offer-log-history.tabs.lane"
        ),
        value: "lane",
      },
    ],
    [t]
  );

  return (
    <section className="h-[420px]">
      <h1 className="mb-5 text-[20px] font-[700]">
        {t<string>("context.private.components.offer-log-history.title")}
      </h1>
      <Tabs
        onChange={setValue}
        defaultTab="approvals"
        tabs={tabs}
        classes={{
          tab: {
            container: "cursor-pointer font-[400] hover:text-black",
            active: "text-black",
            inactive: "text-[#9E9E9E]",
          },
        }}
      >
        {(tabs) => (
          <section className="grid gap-[15px]">
            <section className="grid grid-flow-col items-center justify-start gap-[20px] justify-self-start">
              {tabs}
            </section>
            <section>
              {value === "approvals" && (
                <LogsTable
                  section="history"
                  showStatus={true}
                  carrier={carrier}
                  origin={origin}
                  destination={destination}
                />
              )}
              {value === "lane" && (
                <LogsTable
                  section="orders"
                  showStatus={false}
                  carrier={carrier}
                  origin={origin}
                  destination={destination}
                />
              )}
            </section>
          </section>
        )}
      </Tabs>
    </section>
  );
};
